import React from 'react';
import { bool, func, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../../../util/reactIntl';
import css from './CancellationPolicyAccordion.module.css';

export const CANCELLATION_POLICIES = [
  {
    id: 'flex',
    label: 'Flex',
    description:
      'Free cancellation offered within 48 hours of booking. Receive a full refund if canceled at least 45 days before arrival.',
    groups: [
      {
        label: 'If guest cancels:',
        items: [
          '≥45 days before arrival: full refund.',
          '44–15 days: 50% booking deposit refunded; any remaining payments due refunded.',
          '<15 days: booking deposit and second payment non-refundable; any remaining payments due refunded.',
          'After arrival: 30-day notice to shorten stay; unused time beyond 30 days refunded.',
        ],
      },
      {
        label: 'If host cancels:',
        items: ['Full refund.'],
      },
    ],
  },
  {
    id: 'standard',
    label: 'Standard',
    description:
      'Free cancellation offered within 48 hours of booking. Receive a full refund if canceled at least 60 days before arrival.',
    groups: [
      {
        label: 'If guest cancels:',
        items: [
          '≥60 days before arrival: full refund.',
          '59–30 days: booking deposit non-refundable; any remaining payments due refunded.',
          '<30 days: booking deposit and second payment non-refundable; any remaining payments due refunded.',
          'After arrival: 30-day notice to shorten stay; unused time beyond 30 days refunded.',
        ],
      },
      {
        label: 'If host cancels:',
        items: ['Full refund.'],
      },
    ],
  },
  {
    id: 'firm',
    label: 'Firm',
    description:
      'Free cancellation offered within 48 hours of booking. Receive a full refund if canceled at least 90 days before arrival.',
    groups: [
      {
        label: 'If guest cancels:',
        items: [
          '≥90 days before arrival: full refund.',
          '89–45 days: booking deposit non-refundable; any remaining payments due refunded.',
          '<45 days: booking deposit and second payment non-refundable; any remaining payments due refunded.',
          'After arrival: 30-day notice to shorten; unused time beyond 30 days refunded.',
        ],
      },
      {
        label: 'If host cancels:',
        items: ['Full refund.'],
      },
    ],
  },
];

const CancellationPolicyAccordion = props => {
  const {
    isExpanded,
    onToggle,
    selectedPolicy,
    onPolicySelect,
    className,
    rootClassName,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <div className={css.header} onClick={onToggle}>
        <div className={css.headerLeft}>
          <h3 className={css.title}>
            <FormattedMessage id="EditListingPricingForm.CancellationPolicy" />
          </h3>
        </div>
        <div className={css.headerRight}>
          {selectedPolicy && (
            <span className={css.selectedPolicy}>
              {CANCELLATION_POLICIES.find(p => p.id === selectedPolicy)?.label}
              {' '}
              <FormattedMessage id="EditListingPricingForm.Selected" />
            </span>
          )}
          <button
            className={classNames(css.chevron, { [css.expanded]: isExpanded })}
            type="button"
            aria-label="Toggle cancellation policy"
          >
         <svg width="18" height="10" viewBox="0 0 18 10" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M8.55307 8.13421L1.28852 0.87941C1.21799 0.80851 1.13396 0.75249 1.04139 0.71467C0.948824 0.67684 0.849607 0.65797 0.749618 0.65918C0.649628 0.6604 0.550898 0.68166 0.459276 0.72172C0.367653 0.76178 0.285004 0.81982 0.216217 0.8924C0.0752906 1.04086 -0.00219163 1.23842 0.0002345 1.44311C0.00266254 1.64779 0.0848063 1.84346 0.229214 1.98853L8.031 9.77949C8.101 9.84997 8.18435 9.90578 8.27618 9.94368C8.36801 9.98157 8.46647 10.0008 8.5658 10.0002C8.66514 9.99956 8.76336 9.97916 8.85472 9.94015C8.94608 9.90114 9.02874 9.84432 9.09788 9.77299L17.1076 1.57261C17.2494 1.42581 17.3286 1.2297 17.3286 1.02563C17.3286 0.82156 17.2494 0.62545 17.1076 0.47865C17.038 0.40692 16.9546 0.3499 16.8625 0.31096C16.7704 0.27202 16.6715 0.25195 16.5715 0.25195C16.4715 0.25195 16.3725 0.27202 16.2804 0.31096C16.1883 0.3499 16.105 0.40692 16.0353 0.47865L8.55307 8.13421Z" fill="black"/>
</svg>
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className={css.content}>
          {CANCELLATION_POLICIES.map(policy => (
            <div key={policy.id} className={css.policyOption}>
              <label className={css.policyLabel}>
                <input
                  type="radio"
                  name="cancellation_policy"
                  value={policy.id}
                  checked={selectedPolicy === policy.id}
                  onChange={() => onPolicySelect(policy.id)}
                  className={css.radioInput}
                />
                <span className={css.policyName}>{policy.label}</span>
              </label>
              <p className={css.policyDescription}>{policy.description}</p>
              {(policy.groups || []).map((group, gIdx) => (
                <div key={gIdx} className={css.policyDetailsGroup}>
                  <p className={css.policyDetailsGroupLabel}>{group.label}</p>
                  <ul className={css.policyDetails}>
                    {(group.items || []).map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

CancellationPolicyAccordion.defaultProps = {
  isExpanded: false,
  selectedPolicy: null,
  className: null,
  rootClassName: null,
};

CancellationPolicyAccordion.propTypes = {
  isExpanded: bool,
  onToggle: func.isRequired,
  selectedPolicy: string,
  onPolicySelect: func.isRequired,
  className: string,
  rootClassName: string,
};

export default CancellationPolicyAccordion;
